import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import { initializeNewRelicTrackingContainer } from '@vp/om-newrelic-tracker';
import { BasicPreloader } from '../../shared/BasicPreloader';
import useIdentityContext from '../../../hooks/useIdentityContext';
import useSiteData from '../../../hooks/useSiteData';
import { InternalViewProvider } from '../../contexts/InternalViewContext';
import { InternalViewDesignFeedbackGroupApplication } from './InternalViewDesignFeedbackGroupApplication';
import { DesignFeedbackGroupApplication } from './DesignFeedbackGroupApplication';
import { AnonymousDesignFeedbackGroupApplication } from './AnonymousDesignFeedbackGroupApplication';

export const DesignFeedbackGroupSession = ({ orderId, groupId }) => {
  const {
    allowAnonymousSessions,
    externalAppNavigation: nav,
    locale,
    siteName,
    vistacartTenant: tenant,
  } = useSiteData();
  const { isIdentityInitialized, identity, auth } = useIdentityContext();
  const {
    isShopper,
    isInternalUser,
    isAnonymousUser,
    shopperId,
    anonymousUserId,
  } = identity;

  // Initialize New Relic
  useEffect(() => {
    if (isIdentityInitialized) {
      initializeNewRelicTrackingContainer({
        // omitting actionRoot to keep parity with current events
        // actionRoot: 'om-orders-ui',
        shopperId: shopperId || anonymousUserId,
        locale,
        site: siteName,
        tenant,
      });
    }
  }, [
    isIdentityInitialized,
    anonymousUserId,
    locale,
    shopperId,
    siteName,
    tenant,
  ]);

  // On load of identity, show a spinner
  if (!isIdentityInitialized) {
    return <BasicPreloader isCentered size="super" mt="xl" mb="xl" />;
  }

  // For internal users, show internal view application
  if (isInternalUser) {
    return (
      <InternalViewProvider>
        <InternalViewDesignFeedbackGroupApplication
          orderId={orderId}
          groupId={groupId}
        />
      </InternalViewProvider>
    );
  }

  // For anonymous users...
  if (isAnonymousUser) {
    // On a site that does not allow anonymous sessions...
    if (!allowAnonymousSessions) {
      // Sign them in!
      auth.signIn();
      return <BasicPreloader isCentered size="super" mt="xl" mb="xl" />;
    }
    // On a site that allows anonymous sessions...
    // Show the anonymous design feedback app
    return (
      <AnonymousDesignFeedbackGroupApplication
        orderIdOnPath={orderId}
        groupIdOnPath={groupId}
      />
    );
  }

  // For signed-in shoppers...
  if (isShopper) {
    // Without an order ID...
    if (!orderId) {
      // Redirect to order history
      const orderHistoryUrl = nav.orderHistory;
      window.location.assign(`${orderHistoryUrl}`);
      return <BasicPreloader isCentered size="super" mt="xl" mb="xl" />;
    }
    // if no group id ...
    if (!groupId) {
      // Redirect to order details
      const orderDetailsUrl = nav.orderDetails;
      window.location.assign(`${orderDetailsUrl}?orderId=${orderId}`);
      return <BasicPreloader isCentered size="super" mt="xl" mb="xl" />;
    }

    // For signed-in shoppers with an order ID and line item ID,
    // show the design feedback application
    return (
      <DesignFeedbackGroupApplication
        orderId={orderId}
        groupId={groupId}
        shopperId={shopperId}
      />
    );
  }

  console.warn('Identity is unknown - cannot render design feedback session');
  return null;
};

DesignFeedbackGroupSession.propTypes = {
  orderId: PropTypes.string.isRequired,
  groupId: PropTypes.string.isRequired,
};
